/* oxlint-disable typescript/no-explicit-any -- schema library requires flexible any types */
// Core types and utilities
export type { input, output, infer, Schema, ParseContext } from './core/types'
export type {
  SchemaDescriptor,
  ProtoMetadata,
  ProtoScalarMetadata,
  ProtoScalarType,
  ProtoWireType,
} from './core/types'
export { SchemaError } from './core/errors'
export type { Issue } from './core/errors'
export { getDefaults } from './core/defaults'
export { inspectSchema } from './core/inspection'
export type { SchemaInspection } from './core/inspection'
export { validateSchema, validateSchemaSync } from './core/validation'
export type { SchemaValidationResult } from './core/validation'

// Import all schema constructors
import * as errors from './core/errors'
import { string } from './schemas/string'
import { number, int32, uint32, sint32, float, double } from './schemas/number'
import {
  bigint,
  int64,
  uint64,
  sint64,
  fixed64,
  sfixed64,
} from './schemas/bigint'
import { boolean, _null, _undefined, unknown } from './schemas/primitives'
import { date, timestamp } from './schemas/date'
import { bytes } from './schemas/bytes'
import { literal } from './schemas/literal'
import { _enum } from './schemas/enum'
import { _instanceof, ufileStream } from './schemas/instanceof'
import { ufilePath } from './schemas/ufile-path'
import { array } from './schemas/array'
import { record } from './schemas/record'
import { object } from './schemas/object'
import { union, discriminatedUnion } from './schemas/union'
import {
  refine,
  transform,
  preprocess,
  _default,
  _catch,
  optional,
  nullable,
} from './schemas/wrappers'
import { custom } from './schemas/custom'
import { _function } from './schemas/function'
import { codec, decode, safeDecode, encode, safeEncode } from './schemas/codec'

// Serialization is independent of execution.
export { toJSONSchema } from './to-json-schema'

// Export schema types
export type { StringSchema } from './schemas/string'
export type { NumberSchema } from './schemas/number'
export type { BigintSchema } from './schemas/bigint'
export type { BytesSchema } from './schemas/bytes'
export type {
  BooleanSchema,
  NullSchema,
  UndefinedSchema,
  UnknownSchema,
} from './schemas/primitives'
export type { DateSchema } from './schemas/date'
export type { LiteralSchema } from './schemas/literal'
export type { EnumSchema } from './schemas/enum'
export type { InstanceOfSchema } from './schemas/instanceof'
export type { UfilePathSchema } from './schemas/ufile-path'
export type { ArraySchema } from './schemas/array'
export type { RecordSchema } from './schemas/record'
export type { ObjectSchema, Shape } from './schemas/object'
export type { UnionSchema, DiscriminatedUnionSchema } from './schemas/union'
export type {
  RefineSchema,
  TransformSchema,
  PreprocessSchema,
  DefaultSchema,
  CatchSchema,
  OptionalSchema,
  NullableSchema,
} from './schemas/wrappers'
export type { CustomSchema } from './schemas/custom'
export type { FunctionSchema } from './schemas/function'
export type { CodecSchema } from './schemas/codec'

// Shorthand constructors
function userId() {
  return string().min(1).meta({ type: 'user-id' })
}

const grpc = {
  int32,
  uint32,
  sint32,
  int64,
  uint64,
  sint64,
  fixed64,
  sfixed64,
  float,
  double,
  bytes,
  timestamp,
}

// Main API object
export const s = {
  // Primitives
  string,
  number,
  bigint,
  boolean,
  null: _null,
  undefined: _undefined,
  unknown,
  date,
  userId,
  grpc,

  // Special types
  literal,
  enum: _enum,
  instanceof: _instanceof,
  ufileStream,
  ufilePath,

  // Complex types
  object,
  array,
  record,
  union,
  discriminatedUnion,
  function: _function,

  // Wrappers/modifiers
  refine,
  transform,
  preprocess,
  default: _default,
  catch: _catch,
  optional,
  nullable,
  custom,

  // Codecs
  codec,
  decode,
  safeDecode,
  encode,
  safeEncode,

  // Error class
  SchemaError: errors.SchemaError,
}

// Type inference helpers
import type {
  Schema,
  SchemaDescriptor as _SchemaDescriptor,
  input as InputType,
  output as OutputType,
} from './core/types'
import type { StringSchema as _StringSchema } from './schemas/string'
import type { NumberSchema as _NumberSchema } from './schemas/number'
import type { BigintSchema as _BigintSchema } from './schemas/bigint'
import type { BytesSchema as _BytesSchema } from './schemas/bytes'
import type {
  BooleanSchema as _BooleanSchema,
  NullSchema as _NullSchema,
  UndefinedSchema as _UndefinedSchema,
  UnknownSchema as _UnknownSchema,
} from './schemas/primitives'
import type { DateSchema as _DateSchema } from './schemas/date'
import type { LiteralSchema as _LiteralSchema } from './schemas/literal'
import type { EnumSchema as _EnumSchema } from './schemas/enum'
import type { InstanceOfSchema as _InstanceOfSchema } from './schemas/instanceof'
import type { UfilePathSchema as _UfilePathSchema } from './schemas/ufile-path'
import type { ArraySchema as _ArraySchema } from './schemas/array'
import type { RecordSchema as _RecordSchema } from './schemas/record'
import type {
  ObjectSchema as _ObjectSchema,
  Shape as _Shape,
} from './schemas/object'
import type {
  UnionSchema as _UnionSchema,
  DiscriminatedUnionSchema as _DiscriminatedUnionSchema,
} from './schemas/union'
import type {
  RefineSchema as _RefineSchema,
  TransformSchema as _TransformSchema,
  PreprocessSchema as _PreprocessSchema,
  DefaultSchema as _DefaultSchema,
  CatchSchema as _CatchSchema,
  OptionalSchema as _OptionalSchema,
  NullableSchema as _NullableSchema,
} from './schemas/wrappers'
import type { CustomSchema as _CustomSchema } from './schemas/custom'
import type { FunctionSchema as _FunctionSchema } from './schemas/function'

// Type inference helpers as namespace
export namespace s {
  export type infer<T> = OutputType<T>
  export type input<T> = T extends Schema ? InputType<T> : never
  export type output<T> = T extends Schema ? OutputType<T> : never
  export type SchemaDescriptor = _SchemaDescriptor

  // Schema types
  export type StringSchema = _StringSchema
  export type NumberSchema = _NumberSchema
  export type BigintSchema = _BigintSchema
  export type BytesSchema = _BytesSchema
  export type BooleanSchema = _BooleanSchema
  export type NullSchema = _NullSchema
  export type UndefinedSchema = _UndefinedSchema
  export type UnknownSchema = _UnknownSchema
  export type DateSchema = _DateSchema
  export type LiteralSchema<T extends string | number | boolean> =
    _LiteralSchema<T>
  export type EnumSchema<T extends readonly [string, ...string[]]> =
    _EnumSchema<T>
  export type InstanceOfSchema<T extends new (...args: any) => any> =
    _InstanceOfSchema<T>
  export type UfilePathSchema = _UfilePathSchema
  export type ArraySchema<T> = _ArraySchema<T>
  export type RecordSchema<K extends Schema, V extends Schema> = _RecordSchema<
    K,
    V
  >
  export type ObjectSchema<T extends _Shape> = _ObjectSchema<T>
  export type Shape = _Shape
  export type UnionSchema<T extends readonly [Schema, Schema, ...Schema[]]> =
    _UnionSchema<T>
  export type DiscriminatedUnionSchema<
    K extends string,
    T extends readonly [Schema, Schema, ...Schema[]],
  > = _DiscriminatedUnionSchema<K, T>
  export type RefineSchema<T extends Schema> = _RefineSchema<T>
  export type TransformSchema<T extends Schema, Out> = _TransformSchema<T, Out>
  export type PreprocessSchema<T extends Schema> = _PreprocessSchema<T>
  export type DefaultSchema<T extends Schema> = _DefaultSchema<T>
  export type CatchSchema<T extends Schema> = _CatchSchema<T>
  export type OptionalSchema<T extends Schema> = _OptionalSchema<T>
  export type NullableSchema<T extends Schema> = _NullableSchema<T>
  export type CustomSchema<T> = _CustomSchema<T>
  export type FunctionSchema<
    Input extends readonly Schema[],
    Output extends Schema | undefined,
  > = _FunctionSchema<Input, Output>
}

// Default export
export default s
